import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Form, message } from 'antd';
import { checkCaqhLogin } from 'api/credentialing';
import type { CaqhCheckVo } from 'api/credentialing/types';
import { useCredTargetProvider } from '../../steps/targetProvider';
import s from './s.module.less';

const STALE_DAYS = 120;

// 向导回填时 hydrateDates 会把 detail 里所有 'YYYY-MM-DD' 字符串转成 dayjs（caqhCheck 也不例外），显示前转回来
const fmtDate = (v: unknown): string => {
    if (!v) return '';
    if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
    return String(v).slice(0, 10);
};

type TKind = 'ok' | 'stale' | 'fail' | 'unavailable';

// 原型 caqhResult 的三态文案；UNAVAILABLE 原型没有（后端：不能提示密码错，允许照常保存）
const resultOf = (r: CaqhCheckVo): { kind: TKind; text: string } => {
    if (r.status === 'FAILED')
        return {
            kind: 'fail',
            text: `We could not sign in to CAQH with these details. Check them with CAQH and save again, or payers will stop at the first step.${
                r.reason ? ` CAQH says: ${r.reason}` : ''
            }`,
        };
    if (r.status === 'STALE')
        return {
            kind: 'stale',
            text: `Login works. The profile was last attested on ${fmtDate(r.attestedAt) || 'an unknown date'}, more than ${STALE_DAYS} days ago, so payers treat it as out of date. Re-attest in CAQH ProView and we will check again.`,
        };
    if (r.status === 'UNAVAILABLE')
        return {
            kind: 'unavailable',
            text: 'We could not reach CAQH just now, so this login has not been checked. Try again in a few minutes. You can still continue.',
        };
    return {
        kind: 'ok',
        text: r.attestedAt ? `Login works. The profile was last attested on ${fmtDate(r.attestedAt)}.` : 'Login works.',
    };
};

const OkIcon = () => (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="#16A34A" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
        />
    </svg>
);
const WarnIcon = ({ color }: { color: string }) => (
    <svg viewBox="0 0 20 20" width="18" height="18" fill={color} aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 8a1 1 0 100-2 1 1 0 000 2z"
        />
    </svg>
);
const InfoIcon = () => (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="#60A5FA" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        />
    </svg>
);
const ICON: Record<TKind, React.ReactNode> = {
    ok: <OkIcon />,
    stale: <WarnIcon color="#F59E0B" />,
    fail: <WarnIcon color="#EF4444" />,
    unavailable: <InfoIcon />,
};

// 原型 caqhCheckHTML（10/15）：登录信息不只存下来，点按钮真的去 CAQH 登录一次并看 attestation 新不新。
// 只警告不拦截（provider 可能正在重置密码）。每次检查都是真实登录，失败多了会锁 CAQH 账号，
// 所以只在点按钮时调，检查中禁止重复点。结果存在表单的 caqhCheck（回填来自 verification/detail）；
// 账号或密码改了，旧结果就不再对应，按「还没查过」显示
export default function CaqhCheck() {
    const form = Form.useFormInstance();
    const targetProviderId = useCredTargetProvider();
    const username: string = Form.useWatch('caqhUsername', form) || '';
    const password: string = Form.useWatch('caqhPassword', form) || '';
    const saved: CaqhCheckVo | null | undefined = Form.useWatch('caqhCheck', form);
    const [checking, setChecking] = useState(false);
    // UNAVAILABLE 不存（后端保留之前的结果），只在本次会话里提示
    const [unavailable, setUnavailable] = useState<CaqhCheckVo | null>(null);

    // 结果对应的账号密码：结果（回填或刚查完）落下那一刻的值；之后改了就视为作废。
    // 用 state 而不是 ref：结果落下后要再渲染一次才能显示
    const credsKey = `${username}\u0000${password}`;
    const [checkedFor, setCheckedFor] = useState<string | null>(null);
    useEffect(() => {
        setCheckedFor(saved ? credsKey : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saved]);
    const current = saved && checkedFor === credsKey ? saved : null;
    const shown = unavailable && unavailable.checkedAt === credsKey ? unavailable : current;
    const result = shown ? resultOf(shown) : null;

    const check = async () => {
        if (!username.trim() || !password) return;
        setChecking(true);
        setUnavailable(null);
        try {
            const res: any = await checkCaqhLogin({ username: username.trim(), password }, targetProviderId);
            if (res?.error) {
                message.error(res.error);
                return;
            }
            const vo: CaqhCheckVo | undefined = res?.data?.data;
            if (!vo) return;
            if (vo.status === 'UNAVAILABLE') {
                // checkedAt 借用来记这次提示对应的账号密码，改了就不再显示
                setUnavailable({ ...vo, checkedAt: credsKey });
                return;
            }
            form.setFieldValue('caqhCheck', vo);
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className={s.wrap}>
            <Button className={s.checkBtn} loading={checking} disabled={!username.trim() || !password} onClick={check}>
                {checking ? 'Checking...' : (current && 'Check again') || 'Check with CAQH'}
            </Button>
            {result && (
                <div className={`${s.result} ${s[result.kind]}`}>
                    {ICON[result.kind]}
                    <span>{result.text}</span>
                </div>
            )}
        </div>
    );
}

// Verification 只读视图里的一行（原型 caqhStatusValue）
export const caqhStatusLine = (r?: CaqhCheckVo | null): { text: string; ok: boolean } | null => {
    if (!r) return null;
    if (r.status === 'FAILED') return { text: 'Could not sign in', ok: false };
    if (r.status === 'STALE') return { text: `Works, last attested ${fmtDate(r.attestedAt)}, out of date`, ok: false };
    if (r.status === 'VERIFIED') return { text: `Works, last attested ${fmtDate(r.attestedAt)}`.trim(), ok: true };
    return null;
};
